import type {Messages} from './keys';

/**
 * English inherits from Thai; it is not the source (foundations §8).
 *
 * Same rules apply: short, plain, no corporate-speak; errors say what to do
 * next rather than what went wrong; money copy is calm and absolute.
 *
 * **"Basket" is deliberate, and it does not track the Thai word.** Thai calls
 * the cart `รายการอาหาร` (owner's decision — see the lexicon note in `th.ts`)
 * because "shopping basket" is the wrong picture for a diner sitting at a table.
 * English already has the ordinary, unremarkable word for the same thing, so it
 * keeps it. Inheriting from Thai means inheriting the *meaning*, not the noun.
 */
export const en: Messages = {
  'common.appName': 'pokpokPOS',
  'common.attribution': 'Ordering by pokpokPOS',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.retry': 'Try again',
  'common.continue': 'Continue',
  'common.done': 'Done',
  'common.ok': 'OK',
  'common.loading': 'Loading…',
  'common.askStaff': 'Ask a member of staff',
  'common.table': 'Table {label}',
  'common.viewMenu': 'View menu',
  'common.quantity': 'Quantity',
  'common.item': 'Item',
  'common.items': '{count} items',
  'common.language': 'Language',
  'common.changeLanguage': 'Change language',
  'common.subtotal': 'Subtotal',
  'common.total': 'Total',
  'common.discount': 'Discount',
  'common.serviceCharge': 'Service charge {rate}%',
  'common.rounding': 'Rounding',
  'common.tip': 'Tip',
  'common.vatIncluded': 'Includes {rate}% VAT',
  'common.vatAdded': 'VAT {rate}%',
  'common.free': 'No extra charge',
  'common.noPhoto': 'No photo',
  'common.offline': 'Offline',
  'common.online': 'Connected',
  'common.reconnecting': 'Reconnecting…',
  'common.stale': 'This may not be up to date',
  'common.soldOut': 'Sold out',
  'common.updatedAt': 'Updated {time}',
  'common.minutesAgo': '{minutes} min ago',
  'common.justNow': 'Just now',
  'common.skipToContent': 'Skip to content',

  'reason.eighty_six': 'Kitchen has run out',
  'reason.out_of_stock': 'Out of stock',
  'reason.off_schedule': 'Not served at this time',
  'reason.channel_hidden': 'Not on this menu',
  'reason.deactivated': 'Not available',

  'error.generic': 'That did not go through. Try again.',
  'error.network': 'No connection. Try again.',
  'error.session': 'This QR has expired. Scan it again.',
  'error.forbidden': 'This is not available here. Ask a member of staff.',
  'error.notFound': 'We could not find that.',
  'error.itemUnavailable': 'Some items have sold out.',
  'error.rateLimited': 'Too many taps. Wait a moment and try again.',
  'error.validation': 'Something is missing. Check and try again.',
  'error.conflict': 'This has changed. Take another look.',
  'error.tableHasOpenBill': 'This table already has an order in. See what was ordered.',
  'error.unavailable': 'The restaurant system is not responding. Ask a member of staff.',
  'error.notAvailableHere': 'This restaurant has not turned this on.',

  'q1.welcome': 'Welcome',
  'q1.openingSession': 'Opening the menu…',
  'q1.youAreAt': 'You are at {table}',
  'q1.tableUnknown': 'Order from this table',
  'q1.startOrdering': 'Start ordering',
  'q1.chooseLanguage': 'Choose a language',
  'q1.orderFirstNote': 'Order now, pay at the counter afterwards',
  'q1.payFirstUnavailable': 'This restaurant takes payment first. Order with a member of staff.',
  'q1.retryOpening': 'Try again',

  'q2.title': 'Language',
  'q2.th': 'ไทย',
  'q2.en': 'English',
  'q2.zh': '中文',
  // Was 'Your basket is kept' — passive, and it named a container the guest has
  // not seen yet if they switch language from Q1. Says the reassuring thing
  // instead, which is what the Thai says.
  'q2.cartKept': 'Everything you chose is still there',

  'q3.title': 'Menu',
  'q3.searchLabel': 'Search the menu',
  'q3.searchPlaceholder': 'Search for a dish',
  'q3.searchClear': 'Clear search',
  'q3.searchNoResults': 'Nothing found for “{query}”',
  'q3.searchNoResultsAction': 'Try another word, or pick a category above',
  'q3.allCategories': 'All',
  'q3.featuredHeading': 'Featured',
  'q3.emptyCategory': 'Nothing in this category yet',
  'q3.loading': 'Loading the menu…',
  'q3.menuUnavailable': 'The menu did not load',
  'q3.menuUnavailableAction': 'Try again, or ask a member of staff',
  'q3.channelPriceNote': 'These are the prices for ordering by QR',
  'q3.itemJustSoldOut': '“{item}” has just sold out',
  'q3.viewCart': 'View basket',
  'q3.viewCartWithCount': 'View basket · {count} items',
  'q3.markerSpicy': 'Spicy',
  'q3.markerVerySpicy': 'Very spicy',
  'q3.markerVegetarian': 'Vegetarian',
  'q3.markerVegan': 'Vegan',
  'q3.markerHalal': 'Halal',
  'q3.markerContainsPork': 'Contains pork',
  'q3.markerContainsNuts': 'Contains nuts',
  'q3.markerContainsSeafood': 'Contains seafood',

  'q4.description': 'Description',
  'q4.noDescription': 'No description yet',
  'q4.required': 'Required',
  'q4.optional': 'Optional',
  'q4.chooseExactly': 'Choose {count}',
  'q4.chooseAtLeast': 'Choose at least {count}',
  'q4.chooseUpTo': 'Choose up to {count}',
  'q4.chooseBetween': 'Choose {min}–{max}',
  'q4.requiredUnmet': 'Still to choose',
  'q4.maxReached': 'That is all you can choose',
  'q4.modifierUnavailable': 'This option has run out',
  'q4.increase': 'Add one',
  'q4.decrease': 'Remove one',
  'q4.noteToKitchen': 'Note for the kitchen',
  // Two examples, never one: one reads as the only thing you may ask for.
  'q4.notePlaceholder': 'e.g. no coriander, no long beans',
  'q4.noteHonesty': 'The kitchen may not read your language, or may miss it in a rush',
  'q4.noteUnavailable': 'This restaurant does not take notes for the kitchen',
  'q4.runningPrice': 'Price',
  'q4.addToCart': 'Add to basket',
  'q4.addToCartWithPrice': 'Add to basket · {price}',
  'q4.added': 'Added to basket',
  'q4.soldOutWhileOpen': 'Sorry — “{item}” has just sold out',
  'q4.soldOutAction': 'Please choose something else',
  'q4.backToMenu': 'Back to the menu',

  'q5.title': 'Basket',
  'q5.empty': 'Your basket is empty',
  'q5.emptyAction': 'Go to the menu',
  'q5.remove': 'Remove',
  'q5.removed': 'Removed “{item}”',
  'q5.undo': 'Undo',
  'q5.edit': 'Edit',
  'q5.addMore': 'Add more',
  'q5.yourItems': 'Yours',
  'q5.tableItems': 'Others at this table',
  'q5.notPaidYet': 'Not paid yet',
  'q5.estimatedTotal': 'Estimated total',
  'q5.estimateNote': 'The restaurant works out the final total when you send the order',
  'q5.confirmTitle': 'Send this order?',
  'q5.confirmBody': 'Goes to the kitchen now — {count} items, about {total}. Pay at the counter afterwards.',
  'q5.confirmSend': 'Yes, send it',
  'q5.lineSoldOut': '“{item}” has sold out',
  'q5.lineSoldOutAction': 'Remove from basket',
  'q5.linePriceChanged': '“{item}” is now {price}',
  'q5.linePriceChangedAction': 'Got it',
  'q5.overVenueLimit': 'You can order up to {limit} items at a time',
  'q5.placeOrder': 'Send order',
  'q5.reviewOrder': 'Order now',

  'q7.title': 'Confirm your order',
  'q7.summary': 'Your order',
  'q7.submit': 'Send to the kitchen',
  'q7.submitting': 'Sending…',
  'q7.payLaterNote': 'Goes to the kitchen now. Pay at the counter afterwards.',
  'q7.sentTitle': 'Sent to the kitchen',
  'q7.sentBody': 'The kitchen has your order. Food is on the way — pay at the counter afterwards.',
  'q7.viewStatus': 'See your order',
  'q7.orderAgain': 'Order more',
  'q7.rejectedTitle': 'The restaurant has not taken this order',
  'q7.rejectedAction': 'Ask a member of staff',
  'q7.connectionLostTitle': 'It did not send — no connection',
  'q7.connectionLostAction': 'Send again. It cannot be ordered twice.',
  'q7.queued': 'Waiting to send — this happens on its own',
  'q7.someItemsSoldOut': 'Some items have sold out',
  'q7.backToCart': 'Go back and edit',
  'q7.alreadySent': 'This order was already sent',

  'q8.title': 'Pay',
  'q8.amountDue': 'Amount due',
  'q8.handoffNote':
    'Tap below and you will be taken to ReservePay’s payment page. Choose how to pay there.',
  'q8.methodsNote': 'You will choose how to pay on the next page',
  'q8.leavingNote': 'The payment page belongs to ReservePay, not to the restaurant',
  'q8.pay': 'Go to payment',
  'q8.paying': 'Opening the payment page…',
  'q8.expiresIn': 'Pay within {minutes} minutes',
  'q8.expiresSoon': 'Less than a minute left',
  'q8.nothingToPay': 'This bill is already paid',
  'q8.resume': 'Go back to the payment page',
  'q8.backToBill': 'Back to the bill',
  'q8.payAtCounterInstead': 'Or pay at the counter',
  'q8.unavailableTitle': 'This restaurant does not take QR payments yet',
  'q8.noRedirectTitle': 'The payment page could not be opened',
  'q8.reconcileTitle': 'We do not know whether the payment was started',
  'q8.reconcileBody': 'Do not try again — ask a member of staff to check first',
  'q8.amountChanged': 'The bill has changed. Check the new amount.',

  'q9.title': 'Payment result',
  'q9.checking': 'Checking…',
  'q9.verifiedTitle': 'Payment complete',
  // "No need to show a receipt" is the instruction that lets a guest put the
  // phone away — the point of the whole green card. "Receipt" is the word this
  // dictionary already uses for a สลิป (see `q9.unconfirmedBody`).
  'q9.verifiedBody':
    'The restaurant has ReservePay’s confirmation. There is no need to show a receipt.',
  'q9.unconfirmedTitle': 'We cannot confirm the payment yet',
  'q9.unconfirmedBody':
    'If you have paid, show the receipt at the counter — the restaurant will check and close the bill',
  'q9.paymentTakenTitle': 'ReservePay has confirmed the payment',
  'q9.paymentTakenBody':
    'The restaurant has not closed the bill yet. Show this page at the counter.',
  'q9.failedTitle': 'The payment did not go through',
  'q9.failedBody': 'Try again, or pay at the counter',
  'q9.expiredTitle': 'The payment window has closed',
  'q9.expiredBody': 'You can start a new payment',
  'q9.payAgain': 'Pay again',
  'q9.noAttempt': 'No payment has been started',
  'q9.recheck': 'Check again',
  'q9.startedAt': 'Payment started at {time}',
  'q9.returnNotice': 'A payment of {amount} is waiting to be confirmed',
  'q9.returnNoticeAction': 'See the payment result',
  'q9.refundNote': 'Refunds are handled at the counter only',

  'q12.title': 'Order ahead',
  'q12.takeawayNote': 'Order ahead and collect at the counter',
  'q12.startOrdering': 'Start ordering',
  'q12.pickupNote': 'Order, then wait for your number at the counter',
  'q12.queueLength': '{count} in the queue',
  'q12.queueUnknown': 'The queue length is not known right now',
  'q12.waitEstimate': 'About {minutes} min wait',
  'q12.waitUnknown': 'The wait is not known right now',

  'q14.title': 'Your food',
  'q14.noOrder': 'No order yet',
  'q14.noOrderAction': 'Go to the menu',
  'q14.statePending': 'Waiting for the kitchen',
  'q14.stateFired': 'Kitchen has it',
  'q14.stateInProgress': 'Being cooked',
  'q14.stateReady': 'Ready',
  'q14.stateServed': 'Served',
  'q14.stateCancelled': 'Cancelled',
  'q14.received': 'The restaurant has your order',
  'q14.progressLabel': 'Order progress',
  'q14.elapsed': '{minutes} min so far',
  'q14.perItemHeading': 'Your dishes',
  'q14.staleTitle': 'This may not be up to date',
  'q14.staleBody': 'We cannot reach the kitchen right now. Ask a member of staff.',
  'q14.billLater': 'Pay at the counter afterwards',
  'q14.orderAgain': 'Order more',
  'q14.orderCancelled': 'This order was cancelled',
  'q14.orderVoided': 'The restaurant cancelled this order',

  // --- Q14 · the two asks ---------------------------------------------------
  'q14.callStaff': 'Call staff',
  'q14.callStaffSent': 'Staff have been called',
  'q14.callStaffPending': 'Staff called',
  'q14.requestBill': 'Ask for the bill',
  'q14.requestBillSent': 'The bill has been asked for',
  'q14.requestBillDone': 'Bill requested',
  'q14.askFailed': 'That did not send — try again',

  'q17.title': 'Your order',
  'q17.thanks': 'Thank you',
  'q17.orderNumber': 'Order {number}',
  'q17.placedAt': 'Ordered at {time}',
  'q17.itemsHeading': 'Items',
  'q17.paid': 'Paid',
  'q17.paidAt': 'Paid at {time}',
  'q17.unpaid': 'Not paid yet',
  'q17.balanceDue': 'Still to pay',
  'q17.payAtCounter': 'Pay at the counter',
  'q17.payOnline': 'Pay online',
  'q17.refunded': 'Refunded',
  'q17.partiallyRefunded': 'Partly refunded',
  'q17.venueHeading': 'Restaurant',
  'q17.keepThisPage': 'You can open this page again any time',
  'q17.taxInvoice': 'Tax invoice',
  'q17.taxInvoiceAtCounter': 'Ask at the counter for a tax invoice',
  'q17.notFound': 'We could not find this order',
  'q17.notFoundAction': 'Ask a member of staff',
  'q17.processorDisclosure': 'Payment processed by ReservePay',

  'q19.invalidTitle': 'This QR does not work',
  'q19.expiredTitle': 'This QR has expired',
  'q19.regeneratedTitle': 'This table has a new QR',
  'q19.body': 'Scan the QR on the table again, or ask a member of staff for a new one',
  'q19.rescan': 'Scan again',
  'q19.noQrTitle': 'No QR scanned',
  'q19.noQrBody': 'Open this page by scanning the QR on your table',

  'q20.closedTitle': 'The restaurant is closed',
  'q20.outsideHoursTitle': 'Not open yet',
  'q20.notAcceptingTitle': 'QR ordering is off right now',
  'q20.notAcceptingBody': 'Order with a member of staff',
  'q20.reopensAt': 'Opens again at {time}',
  'q20.reopensUnknown': 'Opening time not given',
  'q20.hoursHeading': 'Opening hours',
  'q20.hoursToday': 'Today {hours}',
  'q20.hoursUnknown': 'Opening hours not given',
  'q20.browseAnyway': 'You can still look at the menu',
  'q20.day.mon': 'Monday',
  'q20.day.tue': 'Tuesday',
  'q20.day.wed': 'Wednesday',
  'q20.day.thu': 'Thursday',
  'q20.day.fri': 'Friday',
  'q20.day.sat': 'Saturday',
  'q20.day.sun': 'Sunday',

  // --- booking · customer web (BookWeb / BookDone / BookManage) ------------
  'book.title': 'Book a table',
  'book.lead': 'Pick a day and time, then add your contact details',
  'book.dateLabel': 'Date',
  'book.timeLabel': 'Time',
  'book.partyLabel': 'Party size',
  'book.nameLabel': 'Name',
  'book.namePlaceholder': 'What should we call you',
  'book.phoneLabel': 'Mobile number',
  'book.phonePlaceholder': '08x-xxx-xxxx',
  'book.today': 'Today',
  'book.pickDate': 'Choose {date}',
  'book.pickTime': 'Choose {time}',
  'book.partyDecrease': 'Fewer people',
  'book.partyIncrease': 'More people',
  'book.partyValueLabel': 'Party of {count}',
  'book.people': '{count} people',
  'book.summary': '{date} {time} · {count} people',
  'book.holdNote': 'The restaurant will confirm your booking',
  'book.submit': 'Confirm booking',
  'book.submitting': 'Booking…',
  'book.needName': 'Add a name first',
  'book.needPhone': 'Add a mobile number first',
  'book.needTime': 'Choose a time first',
  'book.doneTitle': 'Booking confirmed',
  'book.doneBody': 'The restaurant has your booking',
  'book.detailsHeading': 'Booking details',
  'book.fieldDate': 'Date',
  'book.fieldTime': 'Time',
  'book.fieldParty': 'Party',
  'book.fieldName': 'Name',
  'book.fieldPhone': 'Mobile',
  'book.fieldReference': 'Booking code',
  'book.fieldStatus': 'Status',
  'book.saveLinkNote': 'Keep the link to this page to view or cancel your booking later',
  'book.manageAction': 'Manage booking',
  'book.manageTitle': 'Booking',
  'book.manageTitleRef': 'Booking {reference}',
  'book.statusPending': 'Awaiting confirmation',
  'book.statusConfirmed': 'Confirmed',
  'book.statusSeated': 'Seated',
  'book.statusCompleted': 'Completed',
  'book.statusCancelled': 'Cancelled',
  'book.statusNoShow': 'No-show',
  'book.statusUnknown': 'Status unknown',
  'book.cancelHeading': 'Cancel booking',
  'book.cancelNote': 'Free to cancel up to 2 hours before your booking. After that, please call the restaurant.',
  'book.cancelAction': 'Cancel booking',
  'book.cancelling': 'Cancelling…',
  'book.cancelConfirmTitle': 'Cancel this booking?',
  'book.cancelConfirmBody': 'This cannot be undone',
  'book.cancelConfirmYes': 'Yes, cancel',
  'book.keepBooking': 'Keep booking',
  'book.cancelledTitle': 'Booking cancelled',
  'book.cancelledBody': 'The restaurant has your cancellation',
  'book.cannotCancelWindow':
    'It is within 2 hours of your booking, so it can no longer be cancelled here. Please call the restaurant.',
  'book.cannotCancelTerminal': 'This booking can no longer be cancelled',
  'book.cancelWindowError': 'Too late to cancel here. Please call the restaurant.',
  'book.notFoundTitle': 'Booking not found',
  'book.notFoundBody': 'The link may be expired or wrong. Ask the restaurant.',
  // --- Feedback · post-bill rating ----------------------------------------
  'feedback.title': 'How was your meal?',
  'feedback.subtitle': 'Rate it and tell the restaurant',
  'feedback.ratingLabel': 'Rating',
  'feedback.starLabel': '{count} stars',
  'feedback.commentLabel': 'Comment (optional)',
  'feedback.commentHint': 'No name or phone number needed',
  'feedback.commentPlaceholder': 'Anything else you want the restaurant to know?',
  'feedback.ratingRequired': 'Choose a star rating first',
  'feedback.submit': 'Send feedback',
  'feedback.submitting': 'Sending…',
  'feedback.failedTitle': 'Feedback did not send',
  'feedback.failedAction': 'You can try again',
  'feedback.sentTitle': 'Thanks for your feedback',
  'feedback.sentBody': 'The restaurant has received it',
};
